import React, { cloneElement, useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import Tippy from '@tippyjs/react';
import type { ReactElement } from 'react';

import { RoutePath } from '@components/AppRouter/constants';
import { reportingFormsStore } from '@stores/reportingFormsStore';
import { reportingTreeStore } from '@stores/reportingTreeStore';
import type { PagesParamInterface } from '@stores/reportingFormsStore';
import type { StateTreeInterface } from '@stores/reportingTreeStore';
import type { TreeViewInterface } from '@stores/reportingTreeStore/toViewModel';

import styles from './ContentMenu.module.scss';

interface IContentMenu {
  children: ReactElement;
  onClick: () => Promise<ConfigMenu | undefined>;
  disabled?: boolean;
  visibleChildren?: boolean;
  fromTree?: boolean;
}

export interface ConfigMenu {
  pagesParam?: PagesParamInterface[];
  fullSizeInfo?: boolean;
  organizationCode?: string;
  stateTree?: StateTreeInterface;
  breadcrumbs?: string[];
  reportingTree?: TreeViewInterface[];
}

export const ContentMenu = observer(
  ({ children, onClick, disabled, visibleChildren = true, fromTree }: IContentMenu): ReactElement => {
    const [visible, setVisible] = useState(false);
    const { organizationCode, stateTree, reportingTree } = reportingTreeStore;
    const { fullSizeInfo, breadcrumbs, pagesParam } = reportingFormsStore;

    useEffect(() => {
      if (!visibleChildren) setVisible(false);
    }, [visibleChildren]);

    const show = useCallback((event) => {
      event.preventDefault();
      setVisible(true);
    }, []);

    const hide = useCallback(() => setVisible(false), []);

    const onContentClick = useCallback(
      async (event) => {
        event.stopPropagation();
        const config = await onClick();
        if (!config) {
          hide();
          return;
        }

        const storage: ConfigMenu = {
          fullSizeInfo,
          organizationCode: organizationCode ?? '',
          stateTree,
          reportingTree,
        };
        storage.breadcrumbs = fromTree
          ? [...(config?.breadcrumbs ?? [])]
          : [...breadcrumbs, ...(config?.breadcrumbs ?? [])];

        storage.pagesParam = fromTree
          ? [...(config?.pagesParam ?? [])]
          : [...pagesParam, ...(config?.pagesParam ?? [])];

        localStorage.setItem('treeInNewTab', JSON.stringify(storage));
        window.open(RoutePath.REPORTING_TREE, '_blank');
        hide();
      },
      [breadcrumbs, fromTree, fullSizeInfo, hide, onClick, organizationCode, pagesParam, reportingTree, stateTree],
    );

    const content = (
      <div onClick={onContentClick} className={styles.content}>
        Открыть в новой вкладке
      </div>
    );

    return (
      <Tippy
        allowHTML
        interactive
        // placement="right"
        offset={[0, -35]}
        content={content}
        disabled={disabled}
        arrow={false}
        visible={visible}
        onClickOutside={hide}
      >
        {cloneElement(children, { onContextMenu: show })}
      </Tippy>
    );
  },
);
